import { Box, BoxProps, Divider, DividerProps, Stack, StackProps, Tab, TabProps, Tabs, TabsProps, Typography } from '@mui/material';
import { t } from 'i18next';
import { SyntheticEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { SshHandshake } from '../models/SshHandshake';
import { AlgorithmSupportInfo } from '../models/valueObjects/AlgorithmSupportInfo';
import { ClientServerInfo } from '../models/valueObjects/ClientServerInfo';

enum TabIndex {
  KEX = 0,
  HOST_ALG = 1,
  MAC_C_S = 2,
  MAC_S_C = 3,
  SYM_C_S = 4,
  SYM_S_C = 5,
  PROTO = 6,
  VERSION = 7,
  COMMENTS = 8,
}

const TAB_A11Y_NAMES: Record<TabIndex, string> = {
  [TabIndex.KEX]: 'kex',
  [TabIndex.HOST_ALG]: 'hostalg',
  [TabIndex.MAC_C_S]: 'maccs',
  [TabIndex.MAC_S_C]: 'macsc',
  [TabIndex.SYM_C_S]: 'symcs',
  [TabIndex.SYM_S_C]: 'symsc',
  [TabIndex.PROTO]: 'proto',
  [TabIndex.VERSION]: 'version',
  [TabIndex.COMMENTS]: 'comments',
};

const TAB_LABELS = (): Record<TabIndex, string> => ({
  [TabIndex.KEX]: t('handshakes.sshDetails.kex'),
  [TabIndex.HOST_ALG]: t('handshakes.sshDetails.hostAlg'),
  [TabIndex.MAC_C_S]: t('handshakes.sshDetails.macCS'),
  [TabIndex.MAC_S_C]: t('handshakes.sshDetails.macSC'),
  [TabIndex.SYM_C_S]: t('handshakes.sshDetails.symCS'),
  [TabIndex.SYM_S_C]: t('handshakes.sshDetails.symSC'),
  [TabIndex.PROTO]: t('handshakes.sshDetails.protocolVersion'),
  [TabIndex.VERSION]: t('handshakes.sshDetails.softwareVersion'),
  [TabIndex.COMMENTS]: t('handshakes.sshDetails.comments'),
});

const tabsProps: Partial<TabsProps> = {
  orientation: 'vertical',
  sx: { borderRight: 1, borderColor: 'divider' },
  selectionFollowsFocus: true,
};

const tabProps = (tab: TabIndex, id: number): Partial<TabProps> => ({
  id: `${id}-${TAB_A11Y_NAMES[tab]}-tab`,
  'aria-controls': `${id}-${TAB_A11Y_NAMES[tab]}-tabpanel`,
  wrapped: true,
  label: TAB_LABELS()[tab],
  sx: { whiteSpace: 'pre-wrap', minHeight: 'unset', p: 1.5 },
});

const tabPanelProps = (tab: TabIndex, id: number): Partial<BoxProps> => ({
  role: 'tabpanel',
  id: `${id}-${TAB_A11Y_NAMES[tab]}-tabpanel`,
  'aria-labelledby': `${id}-${TAB_A11Y_NAMES[tab]}-tab`,
  sx: { p: 2, width: 1 },
});

const infoColumnWrapperProps: Partial<StackProps> = {
  gap: 2,
  sx: { flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: 1 } },
};

const infoColumnProps: Partial<StackProps> = {
  gap: 1,
  sx: { flex: 1 },
  tabIndex: 0,
  role: 'presentation',
};

const infoDividerProps: Partial<DividerProps> = {
  sx: { display: { xs: 'none', md: 'initial' } },
  orientation: 'vertical',
  flexItem: true,
};

type Props = {
  handshake: SshHandshake;
};

const AlgorithmInfo = memo(({ info }: { info: AlgorithmSupportInfo }) => {
  const selected = info.getSelected();
  const client = info.getClientSupported();
  const server = info.getServerSupported();

  return (
    <Stack sx={{ gap: 2, height: { xs: 'auto', md: 1 } }}>
      <Stack sx={{ gap: 1, flexDirection: 'row', alignItems: 'center' }} tabIndex={0} role="presentation">
        <Typography fontWeight={500}>{t('handshakes.sshDetails.selectedAlgorithm')}:</Typography>
        {selected ? <Interpolation variant="body2">{`<code>${selected}</code`}</Interpolation> : <Typography>{t('unknown')}</Typography>}
      </Stack>
      <Stack {...infoColumnWrapperProps}>
        <Stack {...infoColumnProps}>
          <Typography fontWeight={500}>{t('handshakes.sshDetails.clientSupport')}</Typography>
          {client.length === 0 && t('unknown')}
          {client.map((c, index) => (
            <Interpolation key={index} variant="body2">{`<code>${c}</code`}</Interpolation>
          ))}
        </Stack>
        <Divider {...infoDividerProps} />
        <Stack {...infoColumnProps}>
          <Typography fontWeight={500}>{t('handshakes.sshDetails.serverSupport')}</Typography>
          {server.length === 0 && t('unknown')}
          {server.map((s, index) => (
            <Interpolation key={index} variant="body2">{`<code>${s}</code`}</Interpolation>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
});

const ClientServer = memo(({ info }: { info: ClientServerInfo }) => {
  const client = info.getClient();
  const server = info.getServer();

  return (
    <Stack {...infoColumnWrapperProps}>
      <Stack {...infoColumnProps}>
        <Typography fontWeight={500}>{t('handshakes.sshDetails.client')}</Typography>
        {client ? <Interpolation variant="body2">{`<code>${client}</code`}</Interpolation> : t('unknown')}
      </Stack>
      <Divider {...infoDividerProps} />
      <Stack {...infoColumnProps}>
        <Typography fontWeight={500}>{t('handshakes.sshDetails.server')}</Typography>
        {server ? <Interpolation variant="body2">{`<code>${server}</code`}</Interpolation> : t('unknown')}
      </Stack>
    </Stack>
  );
});

export const SshHandshakeDetail = memo(({ handshake }: Props) => {
  const [value, setValue] = useState(TabIndex.KEX);

  const focusRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // This component is rendered within an expandable row:
    // setting `autoFocus` directly on the `Tab` causes the view to flicker
    // while MUI adjusts the final panel height to fit the content.
    // Focus the first tab at the next rendering tick to prevent it.
    setTimeout(() => focusRef.current?.focus(), 1);
  }, []);

  const handleChange: TabsProps['onChange'] = useCallback((_: SyntheticEvent, value: number) => {
    setValue(value as TabIndex);
  }, []);

  const id = handshake.getId();

  return (
    <Box sx={{ width: 1, display: 'flex' }}>
      <Tabs value={value} onChange={handleChange} {...tabsProps}>
        <Tab {...tabProps(TabIndex.KEX, id)} ref={focusRef} />
        <Tab {...tabProps(TabIndex.HOST_ALG, id)} />
        <Tab {...tabProps(TabIndex.MAC_C_S, id)} />
        <Tab {...tabProps(TabIndex.MAC_S_C, id)} />
        <Tab {...tabProps(TabIndex.SYM_C_S, id)} />
        <Tab {...tabProps(TabIndex.SYM_S_C, id)} />
        <Tab {...tabProps(TabIndex.PROTO, id)} />
        <Tab {...tabProps(TabIndex.VERSION, id)} />
        <Tab {...tabProps(TabIndex.COMMENTS, id)} />
      </Tabs>
      <Box hidden={value !== TabIndex.KEX} {...tabPanelProps(TabIndex.KEX, id)}>
        <AlgorithmInfo info={handshake.getKexAlgorithms()} />
      </Box>
      <Box hidden={value !== TabIndex.HOST_ALG} {...tabPanelProps(TabIndex.HOST_ALG, id)}>
        <AlgorithmInfo info={handshake.getServerHostKeyAlgorithms()} />
      </Box>
      <Box hidden={value !== TabIndex.MAC_C_S} {...tabPanelProps(TabIndex.MAC_C_S, id)}>
        <AlgorithmInfo info={handshake.getClientServerMacAlgorithms()} />
      </Box>
      <Box hidden={value !== TabIndex.MAC_S_C} {...tabPanelProps(TabIndex.MAC_S_C, id)}>
        <AlgorithmInfo info={handshake.getServerClientMacAlgorithms()} />
      </Box>
      <Box hidden={value !== TabIndex.SYM_C_S} {...tabPanelProps(TabIndex.SYM_C_S, id)}>
        <AlgorithmInfo info={handshake.getClientServerSymmetricEncryptionAlgorithms()} />
      </Box>
      <Box hidden={value !== TabIndex.SYM_S_C} {...tabPanelProps(TabIndex.SYM_S_C, id)}>
        <AlgorithmInfo info={handshake.getServerClientSymmetricEncryptionAlgorithms()} />
      </Box>
      <Box hidden={value !== TabIndex.PROTO} {...tabPanelProps(TabIndex.PROTO, id)}>
        <ClientServer info={handshake.getProtocolVersion()} />
      </Box>
      <Box hidden={value !== TabIndex.VERSION} {...tabPanelProps(TabIndex.VERSION, id)}>
        <ClientServer info={handshake.getSoftwareVersion()} />
      </Box>
      <Box hidden={value !== TabIndex.COMMENTS} {...tabPanelProps(TabIndex.COMMENTS, id)}>
        <ClientServer info={handshake.getSoftwareComments()} />
      </Box>
    </Box>
  );
});
